/**
 * CMS SDK - ContentEditor 组件
 * 富文本内容编辑器
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { t } from '../i18n';
import { ContentStatus, Visibility } from '../types';

/**
 * 内容编辑器组件
 * @param {Object} props - 组件属性
 */
export function ContentEditor({
  content = null,
  onSave,
  onPublish,
  onCancel,
  categories = [],
  tags = [],
  loading = false,
  className = '',
}) {
  // 表单状态
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    body: '',
    cover_url: '',
    category_id: '',
    tags: [],
    visibility: Visibility.PUBLIC,
    seo_title: '',
    seo_description: '',
    seo_keywords: '',
    ...content,
  });
  
  const [showSEO, setShowSEO] = useState(false);
  const [errors, setErrors] = useState({});
  const editorRef = useRef(null);

  // 当 content 变化时更新表单
  useEffect(() => {
    if (content) {
      setFormData(prev => ({ ...prev, ...content }));
    }
  }, [content]);

  // 更新表单字段
  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // 清除该字段的错误
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  }, [errors]);

  // 验证表单
  const validate = useCallback(() => {
    const newErrors = {};
    if (!formData.title?.trim()) {
      newErrors.title = t('errorTitleRequired');
    }
    if (!formData.body?.trim()) {
      newErrors.body = t('errorContentRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // 保存草稿
  const handleSaveDraft = useCallback(() => {
    if (!validate()) return;
    onSave?.({ ...formData, status: ContentStatus.DRAFT });
  }, [formData, validate, onSave]);

  // 发布
  const handlePublish = useCallback(() => {
    if (!validate()) return;
    onPublish?.(formData);
  }, [formData, validate, onPublish]);

  // 处理标签选择
  const toggleTag = useCallback((tagId) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tagId)
        ? prev.tags.filter(id => id !== tagId)
        : [...prev.tags, tagId],
    }));
  }, []);

  return (
    <div className={`cms-content-editor ${className}`}>
      {/* 顶部操作栏 */}
      <div className="cms-editor-header">
        <div className="cms-editor-title">
          {content?.id ? t('editContent') : t('createContent')}
        </div>
        <div className="cms-editor-actions">
          <button 
            className="cms-btn cms-btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            {t('cancel')}
          </button>
          <button 
            className="cms-btn cms-btn-outline"
            onClick={handleSaveDraft}
            disabled={loading}
          >
            {t('draft')}
          </button>
          <button 
            className="cms-btn cms-btn-primary"
            onClick={handlePublish}
            disabled={loading}
          >
            {loading ? t('loading') : t('publish')}
          </button>
        </div>
      </div>

      <div className="cms-editor-body">
        {/* 主编辑区 */}
        <div className="cms-editor-main">
          {/* 标题输入 */}
          <div className="cms-form-group">
            <input
              type="text"
              className={`cms-input cms-input-title ${errors.title ? 'cms-input-error' : ''}`}
              placeholder={t('contentTitle')}
              value={formData.title}
              onChange={(e) => updateField('title', e.target.value)}
            />
            {errors.title && (
              <span className="cms-error-text">{errors.title}</span>
            )}
          </div>

          {/* 摘要输入 */}
          <div className="cms-form-group">
            <textarea
              className="cms-input cms-input-summary"
              placeholder={t('contentSummary')}
              value={formData.summary}
              onChange={(e) => updateField('summary', e.target.value)}
              rows={2}
            />
          </div>

          {/* 正文编辑器 */}
          <div className="cms-form-group">
            <div 
              ref={editorRef}
              className={`cms-editor-content ${errors.body ? 'cms-input-error' : ''}`}
            >
              <textarea
                className="cms-textarea"
                placeholder={t('editorPlaceholder')}
                value={formData.body}
                onChange={(e) => updateField('body', e.target.value)}
                rows={20}
              />
            </div>
            {errors.body && (
              <span className="cms-error-text">{errors.body}</span>
            )}
          </div>
        </div>

        {/* 侧边栏 */}
        <div className="cms-editor-sidebar">
          {/* 封面图 */}
          <div className="cms-sidebar-section">
            <h4>{t('contentCover')}</h4>
            <div className="cms-cover-upload">
              {formData.cover_url ? (
                <div className="cms-cover-preview">
                  <img src={formData.cover_url} alt="Cover" />
                  <button 
                    className="cms-cover-remove"
                    onClick={() => updateField('cover_url', '')}
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div className="cms-cover-placeholder">
                  <input
                    type="text"
                    className="cms-input"
                    placeholder="封面图 URL"
                    value={formData.cover_url}
                    onChange={(e) => updateField('cover_url', e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>

          {/* 分类选择 */}
          <div className="cms-sidebar-section">
            <h4>{t('contentCategory')}</h4>
            <select
              className="cms-select"
              value={formData.category_id}
              onChange={(e) => updateField('category_id', e.target.value)}
            >
              <option value="">{t('filter')}</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* 标签选择 */}
          <div className="cms-sidebar-section">
            <h4>{t('contentTags')}</h4>
            <div className="cms-tags-select">
              {tags.map(tag => (
                <button
                  key={tag.id}
                  className={`cms-tag-chip ${formData.tags.includes(tag.id) ? 'active' : ''}`}
                  onClick={() => toggleTag(tag.id)}
                  style={{ 
                    '--tag-color': tag.color,
                    borderColor: formData.tags.includes(tag.id) ? tag.color : undefined,
                    backgroundColor: formData.tags.includes(tag.id) ? `${tag.color}20` : undefined,
                  }}
                >
                  {tag.name}
                </button>
              ))}
            </div>
          </div>

          {/* 可见性设置 */}
          <div className="cms-sidebar-section">
            <h4>{t('visibility')}</h4>
            <select
              className="cms-select"
              value={formData.visibility}
              onChange={(e) => updateField('visibility', e.target.value)}
            >
              <option value={Visibility.PUBLIC}>{t('public')}</option>
              <option value={Visibility.PRIVATE}>{t('private')}</option>
              <option value={Visibility.MEMBERS}>{t('membersOnly')}</option>
              <option value={Visibility.PASSWORD}>{t('password')}</option>
            </select>
          </div>

          {/* SEO 设置 */}
          <div className="cms-sidebar-section">
            <button 
              className="cms-section-toggle"
              onClick={() => setShowSEO(!showSEO)}
            >
              <span>SEO {t('settings')}</span>
              <span className="cms-toggle-icon">{showSEO ? '−' : '+'}</span>
            </button>
            {showSEO && (
              <div className="cms-seo-fields">
                <input
                  type="text"
                  className="cms-input"
                  placeholder={t('seoTitle')}
                  value={formData.seo_title}
                  onChange={(e) => updateField('seo_title', e.target.value)}
                />
                <textarea
                  className="cms-input"
                  placeholder={t('seoDescription')}
                  value={formData.seo_description}
                  onChange={(e) => updateField('seo_description', e.target.value)}
                  rows={2}
                />
                <input
                  type="text"
                  className="cms-input"
                  placeholder={t('seoKeywords')}
                  value={formData.seo_keywords}
                  onChange={(e) => updateField('seo_keywords', e.target.value)}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContentEditor;
